import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Modal,
  Form,
  message,
  Card,
  Avatar,
  Tooltip,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import {
  SearchOutlined,
  CarOutlined,
  UserAddOutlined,
  PhoneOutlined,
  IdcardOutlined,
  BankOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  UserOutlined,
  CreditCardOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import {
  FETCH_ALL_DRIVERS,
  CREATE_DRIVER,
  UPDATE_DRIVER,
  DELETE_DRIVER,
} from "../../services/apiEndPoints";
import { IDriver } from "../types/driver.types";
import axios from "../../services/axiosInstance";

const { Title, Text } = Typography;

const STAT_CARDS = [
  {
    key: "total",
    label: "Total Drivers",
    icon: <CarOutlined />,
    color: "#1677ff",
    bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "#93c5fd",
  },
  {
    key: "withLicense",
    label: "With License",
    icon: <IdcardOutlined />,
    color: "#8b5cf6",
    bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
    border: "#c4b5fd",
  },
  {
    key: "withBank",
    label: "Bank Details",
    icon: <BankOutlined />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    border: "#6ee7b7",
  },
  {
    key: "withLocation",
    label: "With Location",
    icon: <EnvironmentOutlined />,
    color: "#f59e0b",
    bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
    border: "#fcd34d",
  },
];

const DriverTableComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState<IDriver[]>([]);
  const searchInput = useRef<InputRef>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<IDriver | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const drivers = await axios.get(FETCH_ALL_DRIVERS);
      const data = drivers?.data;
      setData(Array.isArray(data) ? data : data?.results ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 300),
    []
  );

  const handleAddDriver = useCallback(async () => {
    try {
      const values = await addForm.validateFields();
      setAddLoading(true);
      await axios.post(CREATE_DRIVER, values);
      message.success("Driver added successfully");
      addForm.resetFields();
      setIsAddModalOpen(false);
      await fetchDrivers();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      if (msg) message.error(msg);
    } finally {
      setAddLoading(false);
    }
  }, [addForm]);

  const handleEditDriver = useCallback(async () => {
    if (!editingDriver) return;
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      await axios.put(UPDATE_DRIVER(editingDriver.driverId), values);
      message.success("Driver updated successfully");
      editForm.resetFields();
      setIsEditModalOpen(false);
      setEditingDriver(null);
      await fetchDrivers();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      if (msg) message.error(msg);
    } finally {
      setEditLoading(false);
    }
  }, [editForm, editingDriver]);

  const handleDelete = useCallback(async (driverId: string) => {
    try {
      await axios.delete(DELETE_DRIVER(driverId));
      message.success("Driver deleted successfully");
      await fetchDrivers();
    } catch {
      message.error("Failed to delete driver");
    }
  }, []);

  const openEditModal = useCallback(
    (driver: IDriver) => {
      setEditingDriver(driver);
      editForm.setFieldsValue({
        name: driver.name,
        phoneNumber: driver.phoneNumber,
        bankAccountNumber: driver.bankAccountNumber || "",
        bankAccountName: driver.bankAccountName || "",
        ifscCode: driver.ifscCode || "",
        panCard: driver.panCard || "",
        licenseNumber: driver.licenseNumber || "",
        location: driver.location || "",
      });
      setIsEditModalOpen(true);
    },
    [editForm]
  );

  const getColumnSearchProps = useCallback(
    (dataIndex: string) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters = () => {},
      }: any) => (
        <div style={{ padding: 8, width: 240 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ marginBottom: 8 }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            >
              Search
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters();
                setSearchText("");
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: any) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value: any, record: IDriver) =>
        record[dataIndex as keyof IDriver]
          ?.toString()
          .toLowerCase()
          .includes(String(value).toLowerCase()),
      render: (text: any) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text?.toString() || ""}
          />
        ) : (
          <span style={{ whiteSpace: "nowrap" }}>{text}</span>
        ),
    }),
    [searchText, searchedColumn, handleSearch]
  );

  const filteredData = useMemo(() => {
    if (!globalSearch) return data;
    return data.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(globalSearch.toLowerCase())
    );
  }, [data, globalSearch]);

  const stats = useMemo(
    () => ({
      total: data.length,
      withLicense: data.filter((d) => d.licenseNumber).length,
      withBank: data.filter((d) => d.bankAccountNumber).length,
      withLocation: data.filter((d) => d.location).length,
    }),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <>
            <UserOutlined style={{ fontSize: 12 }} /> Driver
          </>
        ),
        dataIndex: "name",
        key: "name",
        ...getColumnSearchProps("name"),
        render: (name: string, record: IDriver) => (
          <Space>
            <Avatar
              size={32}
              style={{
                background: "linear-gradient(135deg, #1677ff, #0ea5e9)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </Avatar>
            <div>
              <Text strong style={{ display: "block", lineHeight: 1.3 }}>
                {name}
              </Text>
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", lineHeight: 1.3 }}
              >
                {record.phoneNumber}
              </Text>
            </div>
          </Space>
        ),
      },
      {
        title: (
          <>
            <PhoneOutlined style={{ fontSize: 12 }} /> Phone
          </>
        ),
        dataIndex: "phoneNumber",
        key: "phoneNumber",
        width: 140,
        ...getColumnSearchProps("phoneNumber"),
        render: (phone: string) =>
          phone ? (
            <Text style={{ fontSize: 13 }}>{phone}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <EnvironmentOutlined style={{ fontSize: 12 }} /> Location
          </>
        ),
        dataIndex: "location",
        key: "location",
        width: 140,
        ...getColumnSearchProps("location"),
        render: (location: string) =>
          location ? (
            <Tag icon={<EnvironmentOutlined />} color="blue">
              {location}
            </Tag>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <IdcardOutlined style={{ fontSize: 12 }} /> License
          </>
        ),
        dataIndex: "licenseNumber",
        key: "licenseNumber",
        width: 150,
        render: (license: string) =>
          license ? (
            <Tag icon={<IdcardOutlined />} color="purple">
              {license}
            </Tag>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <BankOutlined style={{ fontSize: 12 }} /> Bank
          </>
        ),
        dataIndex: "bankAccountName",
        key: "bankAccountName",
        width: 150,
        render: (bankName: string, record: IDriver) =>
          bankName || record.bankAccountNumber ? (
            <div>
              {bankName && (
                <Text style={{ fontSize: 13, display: "block", lineHeight: 1.3 }}>
                  {bankName}
                </Text>
              )}
              {record.bankAccountNumber && (
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: "block", lineHeight: 1.3 }}
                >
                  ****{record.bankAccountNumber.slice(-4)}
                </Text>
              )}
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <CreditCardOutlined style={{ fontSize: 12 }} /> PAN
          </>
        ),
        dataIndex: "panCard",
        key: "panCard",
        width: 120,
        render: (pan: string) =>
          pan ? (
            <Text style={{ fontSize: 13 }}>{pan}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <CalendarOutlined style={{ fontSize: 12 }} /> Added
          </>
        ),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 140,
        sorter: (a: IDriver, b: IDriver) =>
          moment(a.createdAt).unix() - moment(b.createdAt).unix(),
        render: (createdAt: string) => (
          <div>
            <Text style={{ whiteSpace: "nowrap", fontSize: 13 }}>
              {moment(createdAt).format("MMM D, YYYY")}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {moment(createdAt).fromNow()}
            </Text>
          </div>
        ),
      },
      {
        title: (
          <>
            <SafetyCertificateOutlined style={{ fontSize: 12 }} /> Actions
          </>
        ),
        key: "actions",
        width: 100,
        align: "center" as const,
        render: (_: any, record: IDriver) => (
          <Space size={4}>
            <Tooltip title="Edit driver">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
            <Tooltip title="Delete driver">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: "Delete Driver",
                    content: `Are you sure you want to delete ${record.name}? This will deactivate the driver.`,
                    okText: "Delete",
                    okType: "danger",
                    onOk: () => handleDelete(record.driverId),
                  })
                }
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [getColumnSearchProps, handleDelete, openEditModal]
  );

  const driverFormFields = (
    <>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Driver Name"
            name="name"
            rules={[{ required: true, message: "Please enter driver name" }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Full name"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: "Please enter phone number" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\d{10}$/.test(value.trim())) return Promise.resolve();
                  return Promise.reject(
                    new Error("Phone number must be exactly 10 digits")
                  );
                },
              },
            ]}
          >
            <Input
              prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
              placeholder="9876543210"
              maxLength={10}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="License Number" name="licenseNumber">
            <Input
              prefix={<IdcardOutlined style={{ color: "#94a3b8" }} />}
              placeholder="DL-1234567890"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="PAN Card" name="panCard">
            <Input
              prefix={<CreditCardOutlined style={{ color: "#94a3b8" }} />}
              placeholder="ABCDE1234F"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Bank Account Name" name="bankAccountName">
            <Input
              prefix={<BankOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Account holder name"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Bank Account Number" name="bankAccountNumber">
            <Input
              prefix={<BankOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Account number"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="IFSC Code" name="ifscCode">
            <Input
              prefix={<BankOutlined style={{ color: "#94a3b8" }} />}
              placeholder="SBIN0001234"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Location" name="location">
            <Input
              prefix={<EnvironmentOutlined style={{ color: "#94a3b8" }} />}
              placeholder="City or area"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {/* Page header */}
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <CarOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Driver Database
          </Title>
          <Text className="page-subtitle">
            Manage driver profiles, documents, and bank details
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Driver
          </Button>
        </Col>
      </Row>

      {/* Stat cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((card) => (
          <Col key={card.key} xs={12} sm={12} md={6}>
            <Card
              className="stat-card"
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
              }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${card.color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.1,
                    }}
                  >
                    {stats[card.key as keyof typeof stats]}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      fontWeight: 500,
                      lineHeight: 1.3,
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Toolbar + Table */}
      <div>
        <div className="table-toolbar">
          <Space wrap size={8}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Search drivers..."
              style={{ width: 260 }}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
          </Space>
        </div>

        <Table<IDriver>
          rowKey="driverId"
          size="middle"
          columns={columns as any}
          dataSource={filteredData}
          loading={{
            spinning: loading,
            tip: "Loading drivers...",
          }}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0" }}>
                <CarOutlined
                  style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
                />
                <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
                  No drivers found
                </div>
                <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                  {globalSearch
                    ? "Try adjusting your search"
                    : "Add a driver to get started"}
                </div>
              </div>
            ),
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) => (
              <Text style={{ color: "#64748b", fontSize: 13 }}>
                Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
                <Text strong>{total}</Text> driver{total !== 1 ? "s" : ""}
              </Text>
            ),
          }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* Add Driver Modal */}
      <Modal
        title={
          <>
            <UserAddOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Add New Driver
          </>
        }
        open={isAddModalOpen}
        onCancel={() => {
          setIsAddModalOpen(false);
          addForm.resetFields();
        }}
        onOk={handleAddDriver}
        okText="Add Driver"
        confirmLoading={addLoading}
        width={600}
      >
        <Form form={addForm} layout="vertical" requiredMark={false}>
          {driverFormFields}
        </Form>
      </Modal>

      {/* Edit Driver Modal */}
      <Modal
        title={
          <>
            <EditOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Edit Driver
          </>
        }
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingDriver(null);
          editForm.resetFields();
        }}
        onOk={handleEditDriver}
        okText="Save Changes"
        confirmLoading={editLoading}
        width={600}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {editingDriver?.name} — {editingDriver?.phoneNumber}
        </Text>
        <Form form={editForm} layout="vertical" requiredMark={false}>
          {driverFormFields}
        </Form>
      </Modal>
    </div>
  );
};

export default DriverTableComponent;
